/**
 * Uses Swiper package to create a carousel for product media
 * More info: https://swiperjs.com/
 */
class ProductMedia extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      slider: '[data-slider]',
      thumbs: '[data-thumbs]',
      mediaItem: '[data-media-id]',
    };
    this.selectedMediaIndex =
      Number(
        this.querySelector(this.selectors.slider)?.querySelector(
          '[data-selected]',
        )?.dataset.index,
      ) || 0;
    this.settings = {
      elements: {
        slider: this.querySelector(this.selectors.slider),
        thumbs: this.querySelector(this.selectors.thumbs),
      },
      instances: {
        slider: null,
        thumbs: null,
      },
      options: {
        slider: {
          initialSlide: this.selectedMediaIndex,
          spaceBetween: 20,
          watchOverflow: true,
          autoHeight: true,
          navigation: {
            prevEl: this.querySelector('.swiper-arrow--prev'),
            nextEl: this.querySelector('.swiper-arrow--next'),
          },
          pagination: {
            el: '.swiper-pagination',
            type: 'bullets',
            clickable: true,
          },
        },
        thumbs: {
          initialSlide: this.selectedMediaIndex,
          slidesPerView: 'auto',
          spaceBetween: 12,
          direction: 'vertical',
          freeMode: true,
          navigation: {
            prevEl: this.querySelector('.swiper-arrow--thumbs-prev'),
            nextEl: this.querySelector('.swiper-arrow--thumbs-next'),
          },
        },
      },
    };
    // Store original thumb slides for reference
    this.originalMainSlides = null;
    this.originalThumbSlides = null;
    // Track currently displayed color group
    this.currentColorGroup = null;
    // PhotoSwipe (when zoom is enabled)
    this.PhotoSwipe = null;
    this.pswp = null;
  }

  connectedCallback() {
    this.init();
    this.initPhotoSwipe();
  }

  init() {
    if (!this.settings.elements.slider || !this.settings.elements.thumbs)
      return;

    // Store original thumb slides before any filtering
    const thumbsWrapper = this.settings.elements.thumbs.querySelector('.swiper-wrapper');
    if (thumbsWrapper) {
      this.originalThumbSlides = Array.from(thumbsWrapper.querySelectorAll('.swiper-slide'));
    }

    // Store original thumb slides before any filtering
    const sliderWrapper = this.settings.elements.slider.querySelector('.swiper-wrapper');
    if (sliderWrapper) {
      this.originalMainSlides = Array.from(sliderWrapper.querySelectorAll('.swiper-slide'));
    }

    this.settings.instances.thumbs = new Swiper(
      this.settings.elements.thumbs,
      this.settings.options.thumbs,
    );
    this.settings.options.slider.thumbs = {
      swiper: this.settings.instances.thumbs,
    };

    this.settings.instances.slider = new Swiper(
      this.settings.elements.slider,
      this.settings.options.slider,
    );

    // Reset videos when slide changes
    this.settings.instances.slider.on('slideChange', () => {
      this.resetDeferredMedia();
    });

    // Dispatch event to signal that slider is ready
    this.dispatchEvent(new CustomEvent('product-media-ready', { bubbles: true }));

    // Update thumbnail arrows visibility (with small delay to ensure Swiper is fully initialized)
    this.updateThumbArrowsVisibility(50);
  }

  /**
   * Initialize PhotoSwipe for product image zoom (when enable_zoom uses PhotoSwipe).
   * Binds click on .product__gallery-opener[data-pswp-src] to open PhotoSwipe at the correct index.
   */
  async initPhotoSwipe() {
    const openers = this.querySelectorAll('.product__gallery-opener[data-pswp-src]');
    if (openers.length === 0) return;

    if (!this.PhotoSwipe) {
      if (window.PhotoSwipe) {
        this.PhotoSwipe = window.PhotoSwipe;
      } else {
        try {
          const photoSwipeScript = document.querySelector('script[src*="photoswipe.esm.min.js"]');
          if (photoSwipeScript) {
            const scriptSrc = photoSwipeScript.getAttribute('src');
            const module = await import(scriptSrc);
            this.PhotoSwipe = module.default || module;
          } else {
            const module = await import('./photoswipe.esm.min.js');
            this.PhotoSwipe = module.default || module;
          }
        } catch (error) {
          console.error('Failed to load PhotoSwipe:', error);
          setTimeout(() => this.initPhotoSwipe(), 200);
          return;
        }
      }
    }

    this.addEventListener('click', (e) => {
      const opener = e.target.closest('.product__gallery-opener[data-pswp-src]');
      if (!opener) return;
      e.preventDefault();
      const openersList = this.querySelectorAll('.product__gallery-opener[data-pswp-src]');
      const index = Array.from(openersList).indexOf(opener);
      if (index !== -1) this.openPhotoSwipe(index);
    });
  }

  /**
   * Build gallery items from current DOM and open PhotoSwipe at the given index.
   * @param {number} index - Index of the image to open
   */
  openPhotoSwipe(index) {
    const openers = this.querySelectorAll('.product__gallery-opener[data-pswp-src]');
    if (openers.length === 0) return;

    const galleryItems = Array.from(openers).map((el) => ({
      src: el.getAttribute('data-pswp-src'),
      width: parseInt(el.getAttribute('data-pswp-width'), 10) || 0,
      height: parseInt(el.getAttribute('data-pswp-height'), 10) || 0,
    }));

    if (!this.PhotoSwipe) return;

    const options = {
      dataSource: galleryItems,
      index: Math.min(index, galleryItems.length - 1),
      showHideAnimationType: 'zoom',
      zoomAnimationDuration: 400,
      bgOpacity: 0.9,
      spacing: 0.12,
      easing: 'cubic-bezier(0.4, 0, 0.22, 1)',
    };

    this.pswp = new this.PhotoSwipe(options);
    this.pswp.init();
  }

  /**
   * Show/hide thumbnail arrows based on slide count
   * Hide arrows if there are 6 or fewer slides (since slidesPerView is 6)
   * @param {Number} delay - Optional delay in milliseconds to wait for Swiper to update
   * @returns {Void}
   */
  updateThumbArrowsVisibility(delay = 0) {
    const updateVisibility = () => {
      const thumbsArrows = this.querySelector('.swiper-arrows--thumbs');
      const thumbsContainerSlider = this.querySelector('[data-thumbs]');

      if (!thumbsArrows || !thumbsContainerSlider) {
        return;
      }

      const maxHeightNew = 693;
      const thumbsContainerSliderHeight = thumbsContainerSlider.scrollHeight;

      if (thumbsContainerSliderHeight <= maxHeightNew) {
        thumbsArrows.classList.add('hidden');
      } else {
        thumbsArrows.classList.remove('hidden');
      }
    };

    if (delay > 0) {
      setTimeout(updateVisibility, delay);
    } else {
      updateVisibility();
    }
  }

  /**
   * Check if slider is initialized and ready
   * @returns {Boolean}
   */
  isReady() {
    return !!(
      this.settings.instances.slider &&
      this.settings.instances.thumbs &&
      this.settings.elements.slider &&
      this.settings.elements.thumbs
    );
  }

  /**
   * Reset all deferred-media elements to their initial state (stop videos and show play button)
   */
  resetDeferredMedia() {
    const deferredMediaElements = this.querySelectorAll('deferred-media[loaded]');

    deferredMediaElements.forEach((deferredMedia) => {
      // Pause any playing videos/iframes
      const iframe = deferredMedia.querySelector('iframe');
      const video = deferredMedia.querySelector('video');

      if (iframe) {
        // Pause YouTube videos
        if (iframe.classList.contains('js-youtube')) {
          iframe.contentWindow?.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*',
          );
        }
        // Pause Vimeo videos
        if (iframe.classList.contains('js-vimeo')) {
          iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        }
      }

      if (video) {
        video.pause();
      }

      // Remove the loaded wrapper and reset state
      const wrapper = deferredMedia.querySelector('.deferred-media__wrapper');
      if (wrapper) {
        wrapper.remove();
      }

      // Remove loaded attribute to show play button again
      deferredMedia.removeAttribute('loaded');
    });
  }

  setActiveMedia(id) {
    // Search only within the current active slider, not all slides in the component
    const mainSliderWrapper = this.settings.elements.slider?.querySelector('.swiper-wrapper');
    if (!mainSliderWrapper) {
      return;
    }

    const mediaFound = Array.from(
      mainSliderWrapper.querySelectorAll(this.selectors.mediaItem),
    ).find((media) => Number(media.dataset.mediaId) === id);

    if (!mediaFound) {
      return;
    }

    this.settings.instances.slider.slideTo(Number(mediaFound.dataset.index));
  }

  /**
   * Filter slides by color - checks if normalized color exists in slide's color list
   * @param {Array} slides - Array of slide elements to filter
   * @param {String} normalizedColor - Normalized color value (lowercase, trimmed)
   * @returns {Array} - Filtered array of slides matching the color
   */
  filterSlidesByColor(slides, normalizedColor) {
    return slides.filter((slide) => {
      const colorsString = slide.dataset.color?.trim().toLowerCase() || '';
      if (!colorsString) return false;

      // Split by comma and check if normalized color is in the list
      const colorsArray = colorsString.split(',').map(c => c.trim());
      return colorsArray.includes(normalizedColor);
    });
  }

  /**
   * Filter media by color - replaces slider content with matching color group
   * @param {String} color - Color value to filter by (e.g., "Green")
   * @returns {Void}
   */
  filterMediaByColor(color) {
    if (!this.settings.elements.slider || !this.settings.elements.thumbs) {
      return;
    }

    // Find the color groups container
    const colorGroupsContainer = document.querySelector('[data-color-groups]');
    if (!colorGroupsContainer) {
      return;
    }

    // Normalize color for comparison (case-insensitive, trimmed)
    const normalizedColor = color ? color.trim().toLowerCase() : '';

    // Find the matching color group
    let colorGroup = null;
    let isUsingAllGroup = false;

    if (normalizedColor) {
      const allColorGroups = colorGroupsContainer.querySelectorAll('[data-color-group]');
      colorGroup = Array.from(allColorGroups).find((group) => {
        const groupColor = group.dataset.colorGroup?.trim().toLowerCase();
        return groupColor === normalizedColor;
      });

      // If no matching color group found, fall back to "all" group
      if (!colorGroup) {
        colorGroup = colorGroupsContainer.querySelector('[data-color-group="all"]');
        isUsingAllGroup = true;
      }
    } else {
      // If no color provided, use "all" group
      colorGroup = colorGroupsContainer.querySelector('[data-color-group="all"]');
      isUsingAllGroup = true;
    }

    if (!colorGroup) {
      return;
    }

    // Get the color group identifier
    const targetGroupColor = colorGroup.dataset.colorGroup?.trim().toLowerCase() || 'all';

    // If we're already displaying the "all" group and the new selection also requires "all", skip update
    if (this.currentColorGroup === 'all' && isUsingAllGroup) {
      return;
    }

    // Disable/destroy sliders before updating content
    this.disableSliders();

    // Get original thumb slides (stored on init or get from current container)
    const allOriginalMainSlides = this.originalMainSlides ||
      Array.from(this.settings.elements.slider.querySelectorAll('.swiper-slide'));
    const allOriginalThumbSlides = this.originalThumbSlides ||
      Array.from(this.settings.elements.thumbs.querySelectorAll('.swiper-slide'));

    // Find matching slides by color (supporting multiple colors per image)
    let matchingSlides = [];
    let matchingThumbSlides = [];

    if (isUsingAllGroup) {
      matchingSlides = allOriginalMainSlides;
      matchingThumbSlides = allOriginalThumbSlides;
    } else {
      matchingSlides = this.filterSlidesByColor(allOriginalMainSlides, normalizedColor);
      matchingThumbSlides = this.filterSlidesByColor(allOriginalThumbSlides, normalizedColor);
    }

    const mainSliderWrapper = this.settings.elements.slider.querySelector('.swiper-wrapper');
    const thumbsWrapper = this.settings.elements.thumbs.querySelector('.swiper-wrapper');

    if (!mainSliderWrapper) {
      this.enableSliders();
      return;
    }

    // Clear existing slides
    mainSliderWrapper.innerHTML = '';
    thumbsWrapper.innerHTML = '';

    // Clone and append slides from color group
    matchingSlides.forEach((slide, index) => {
      const clonedSlide = slide.cloneNode(true);
      clonedSlide.dataset.index = index;
      mainSliderWrapper.appendChild(clonedSlide);
    });

    matchingThumbSlides.forEach((slide, index) => {
      const clonedThumb = slide.cloneNode(true);
      clonedThumb.dataset.index = index;
      thumbsWrapper.appendChild(clonedThumb);
    });

    // Reinitialize sliders (initialSlide is set in enableSliders)
    this.enableSliders();

    // Tell lazysizes to process cloned images (avoids race where clones have data-src but no src)
    this.unveilLazyImages();

    // Update the current color group tracking
    this.currentColorGroup = targetGroupColor;

    // Update thumbnail arrows visibility after filtering
    // Use setTimeout to ensure Swiper is fully initialized
    setTimeout(() => {
      this.updateThumbArrowsVisibility(50);
    }, 50);
  }

  /**
   * Trigger lazysizes to process .lazyload images in this element (e.g. after cloning slides).
   * Ensures cloned images get data-src copied to src so they load.
   */
  unveilLazyImages() {
    if (!window.lazySizes) return;
    const lazyImages = this.querySelectorAll('.lazyload');
    lazyImages.forEach((img) => {
      window.lazySizes.loader.unveil(img);
    });
  }

  /**
   * Disable/destroy slider instances before content update
   * @returns {Void}
   */
  disableSliders() {
    if (this.settings.instances.slider) {
      this.settings.instances.slider.destroy(true, true);
      this.settings.instances.slider = null;
    }
    if (this.settings.instances.thumbs) {
      this.settings.instances.thumbs.destroy(true, true);
      this.settings.instances.thumbs = null;
    }
  }

  /**
   * Reinitialize slider instances after content update
   * @returns {Void}
   */
  enableSliders() {
    if (!this.settings.elements.slider || !this.settings.elements.thumbs) {
      return;
    }

    // Calculate initial slide index from current slides
    const mainSliderWrapper = this.settings.elements.slider.querySelector('.swiper-wrapper');
    const selectedSlide = mainSliderWrapper?.querySelector('[data-selected]');
    const initialSlideIndex = selectedSlide
      ? Number(selectedSlide.dataset.index) || 0
      : 0;

    // Update thumbs options with new initial slide and navigation
    const thumbsOptions = {
      ...this.settings.options.thumbs,
      initialSlide: initialSlideIndex,
      navigation: {
        prevEl: this.querySelector('.swiper-arrow--thumbs-prev'),
        nextEl: this.querySelector('.swiper-arrow--thumbs-next'),
      },
    };

    // Reinitialize thumbs first
    this.settings.instances.thumbs = new Swiper(
      this.settings.elements.thumbs,
      thumbsOptions,
    );

    // Update slider options to link thumbs and set initial slide
    const sliderOptions = {
      ...this.settings.options.slider,
      initialSlide: initialSlideIndex,
      thumbs: {
        swiper: this.settings.instances.thumbs,
      },
    };

    // Reinitialize main slider
    this.settings.instances.slider = new Swiper(
      this.settings.elements.slider,
      sliderOptions,
    );

    // Reset videos when slide changes
    this.settings.instances.slider.on('slideChange', () => {
      this.resetDeferredMedia();
    });

    // Dispatch event to signal that slider is ready
    this.dispatchEvent(new CustomEvent('product-media-ready', { bubbles: true }));

    // Update thumbnail arrows visibility (with small delay to ensure Swiper is fully initialized)
    this.updateThumbArrowsVisibility(50);
  }
}

customElements.define('product-media', ProductMedia);
